import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function isSameDay(a, b) { return a.toDateString() === b.toDateString(); }
function pad(n) { return String(n).padStart(2, "0"); }
function dateKey(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

export function EventCalendar({ events = [], onEventClick, onDateClick }) {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const today = new Date();
  const todayDow = today.getDay();

  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const start = new Date(first);
    start.setDate(start.getDate() - first.getDay());
    const end = new Date(last);
    end.setDate(end.getDate() + (6 - last.getDay()));
    const days = [];
    const d = new Date(start);
    while (d <= end) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return days;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const k = e.event_date;
      if (!map[k]) map[k] = [];
      map[k].push(e);
    });
    return map;
  }, [events]);

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => setCursor(startOfMonth(new Date()));

  return (
    <div className="calendar-shell p-4 sm:p-6 space-y-5" data-testid="event-calendar">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="icon-tile icon-tile-teal h-9 w-9">
            <CalendarDays className="h-4 w-4" strokeWidth={1.7} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Calendar</p>
            <h2 className="text-lg sm:text-xl font-heading font-semibold tracking-tight leading-none mt-0.5" data-testid="calendar-month-title">
              {MONTHS[cursor.getMonth()]} <span className="text-muted-foreground font-medium">{cursor.getFullYear()}</span>
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={goPrev} className="cal-nav-btn !w-9 !px-0" aria-label="Previous month" data-testid="calendar-prev">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" onClick={goToday} className="cal-nav-btn" data-testid="calendar-today">
            Today
          </button>
          <button type="button" onClick={goNext} className="cal-nav-btn !w-9 !px-0" aria-label="Next month" data-testid="calendar-next">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Weekdays row */}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 border-b border-border/40 -mb-2 pb-1">
        {WEEKDAYS.map((w, i) => (
          <div
            key={w}
            className={`calendar-weekday ${isSameDay(cursor, startOfMonth(today)) && i === todayDow ? "calendar-weekday-today" : ""}`}
          >
            {w}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {grid.map((d, idx) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const dayEvents = eventsByDay[dateKey(d)] || [];
          const isToday = isSameDay(d, today);
          const visible = dayEvents.slice(0, 2);
          const more = dayEvents.length - visible.length;

          return (
            <div
              key={idx}
              className={`day-cell ${isToday ? "day-cell-today" : ""} ${!inMonth ? "day-cell-outside" : ""}`}
              onClick={() => onDateClick?.(d)}
              data-testid={`day-cell-${dateKey(d)}`}
            >
              {isToday && <span className="day-today-label">Today</span>}
              <div className="flex items-center">
                {isToday ? (
                  <span className="day-number-today">{d.getDate()}</span>
                ) : (
                  <span className={`text-sm font-semibold ${inMonth ? "text-foreground" : "text-muted-foreground"}`}>
                    {d.getDate()}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1 mt-0.5">
                {visible.map((e) => {
                  const isFree = e.category === "percuma";
                  const chipClass = isFree
                    ? "bg-teal/15 text-teal border border-teal/35 hover:bg-teal/25"
                    : "bg-gold/15 text-gold border border-gold/35 hover:bg-gold/25";
                  return (
                    <button
                      key={e.id}
                      onClick={(ev) => { ev.stopPropagation(); onEventClick?.(e); }}
                      className={`event-chip ${chipClass}`}
                      data-testid={`event-chip-${e.id}`}
                    >
                      <span className="event-chip-dot" />
                      {e.start_time && <span className="font-bold">{e.start_time}</span>}
                      <span className="truncate">{e.short_title || e.title}</span>
                    </button>
                  );
                })}
                {more > 0 && (
                  <span className="text-[10px] font-medium text-muted-foreground px-1.5 py-0.5 rounded-md bg-muted/40 border border-border/40 self-start">
                    +{more} more
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
